import { type Ref } from 'vue'
import { useAppStore } from '@/core/stores/useAppStore'
import { getErrorMessage } from '@/core/types/errors'
import { useStoreItemUpdate } from '@/core/composables/useStoreItemUpdate'
import { i18n } from '@/locales'

/**
 * Options for store operation execution
 */
export interface StoreOperationOptions {
  /**
   * Success message to display in notification
   */
  successMessage?: string

  /**
   * Error notification title (defaults to translated 'Error')
   */
  errorTitle?: string

  /**
   * Whether to show error alert modal (defaults to true)
   */
  showErrorAlert?: boolean

  /**
   * Whether to show success notification (defaults to true if successMessage provided)
   */
  showSuccessNotification?: boolean
}

/**
 * Base store state interface
 */
export interface StoreBaseState {
  loading: Ref<boolean>
  error: Ref<unknown>
}

/**
 * Extended store state with items and currentItem for update operations
 */
export interface StoreBaseStateWithItems<T extends { id: string }> extends StoreBaseState {
  items: Ref<T[]>
  currentItem: Ref<T | null>
}

/**
 * Composable for wrapping store operations with consistent error handling and loading states
 *
 * @example
 * ```typescript
 * const { execute } = useStoreOperation(base)
 *
 * const result = await execute(
 *   async () => await api.fetchData(),
 *   { successMessage: 'Data načtena' }
 * )
 * ```
 */
export function useStoreOperation(base: StoreBaseState) {
  const appStore = useAppStore()

  /**
   * Execute an async operation with automatic error handling and loading state management
   *
   * @param operation - Async function to execute
   * @param options - Operation options
   * @returns Result of the operation, or null if operation failed
   */
  async function execute<T>(
    operation: () => Promise<T>,
    options: StoreOperationOptions = {}
  ): Promise<T | null> {
    const {
      successMessage,
      errorTitle = i18n.global.t('errors.generic.title'),
      showErrorAlert = true,
      showSuccessNotification = !!successMessage
    } = options

    try {
      base.loading.value = true
      base.error.value = undefined

      const result = await operation()

      if (showSuccessNotification && successMessage) {
        appStore.addNotification(successMessage, 'success')
      }

      return result
    } catch (err: unknown) {
      // Store the entire error object for type-safe error checking
      base.error.value = err

      const errorMessage = getErrorMessage(err)

      if (showErrorAlert) {
        appStore.showAlert({
          title: errorTitle,
          message: errorMessage,
          confirmType: 'danger',
          onConfirm: () => {}
        })
      }

      return null
    } finally {
      base.loading.value = false
    }
  }

  /**
   * Execute operation without error alert (only sets error state)
   */
  async function executeSilent<T>(
    operation: () => Promise<T>,
    options: Omit<StoreOperationOptions, 'showErrorAlert'> = {}
  ): Promise<T | null> {
    return execute(operation, { ...options, showErrorAlert: false })
  }

  return {
    execute,
    executeSilent
  }
}

/**
 * Extended composable for operations that update store items
 * Combines execute() with automatic store update via updateItemInStore()
 *
 * @example
 * ```typescript
 * const { executeWithStoreUpdate } = useStoreOperationWithUpdate(base)
 *
 * const result = await executeWithStoreUpdate(
 *   () => api.updateStatus(id, status, projectId),
 *   id,
 *   { successMessage: 'Status byl aktualizován' }
 * )
 * ```
 */
export function useStoreOperationWithUpdate<T extends { id: string }>(
  base: StoreBaseStateWithItems<T>
) {
  const { execute, executeSilent } = useStoreOperation(base)
  const { updateItemInStore } = useStoreItemUpdate()

  /**
   * Execute an async operation that returns an item, automatically updating it in the store
   *
   * @param apiCall - Async function that returns the updated item
   * @param id - ID of the item being updated
   * @param options - Operation options
   * @returns Result of the operation, or null if operation failed
   */
  async function executeWithStoreUpdate(
    apiCall: () => Promise<T>,
    id: string,
    options: StoreOperationOptions = {}
  ): Promise<T | null> {
    return execute(
      async () => {
        const result = await apiCall()
        updateItemInStore(base.items, base.currentItem, id, result)
        return result
      },
      options
    )
  }

  /**
   * Execute update operation without error alert
   */
  async function executeWithStoreUpdateSilent(
    apiCall: () => Promise<T>,
    id: string,
    options: Omit<StoreOperationOptions, 'showErrorAlert'> = {}
  ): Promise<T | null> {
    return executeWithStoreUpdate(apiCall, id, { ...options, showErrorAlert: false })
  }

  return {
    execute,
    executeSilent,
    executeWithStoreUpdate,
    executeWithStoreUpdateSilent
  }
}
